import React, { useState, useEffect } from 'react';
import { FaCheckCircle, FaTimesCircle, FaUser, FaBuilding, FaUserTie,    FaSync   } from 'react-icons/fa';
import api from '../../../src/api/axios';
import RequestCard from '../../components/RequestCard/RequestCard';
import { readStoredUser } from '../../utilities/currentUser';
import './ApproverDashboard.css';

// The server already leaves your own requests out of this list; this is a second guard.
const isOwnRequest = (request) => {
  const me = readStoredUser();
  return Boolean(me?.email) && request?.requester_email === me.email;
};

const ApproverDashboard = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [action, setAction] = useState(''); // 'approve' or 'reject'
  const [rejectionReason, setRejectionReason] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchRequests();
  }, []);

  useEffect(() => {
    applyFilter();
  }, [filter, requests]);

  const fetchRequests = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/api/role-requests/pending');
      setRequests(response.data);
      setError('');
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || 'Failed to load pending requests.';
      console.error('Error fetching pending requests:', err.response?.data ?? err);
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilter = () => {
    if (filter === 'all') {
      setFilteredRequests(requests);
    } else {
      // Filter by company name (simplified - you might want to filter by company ID)
      setFilteredRequests(requests.filter(r => 
        r.company.toLowerCase().includes(filter.toLowerCase())
      ));
    }
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    setAction('approve');
    setRejectionReason('');
    setShowModal(true);
  };

  const handleReject = (request) => {
    setSelectedRequest(request);
    setAction('reject');
    setRejectionReason('');
    setShowModal(true);
  };

  const handleSubmitAction = async () => {
    if (!selectedRequest) return;

    if (action === 'reject' && !rejectionReason.trim()) {
      setError('Please provide a rejection reason (optional but recommended).');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        action: action,
      };

      if (action === 'reject' && rejectionReason.trim()) {
        payload.rejection_reason = rejectionReason;
      }

      await api.post(`/api/role-approval/${selectedRequest.id}`, payload);

      setSuccess(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully.`);
      setShowModal(false);
      setSelectedRequest(null);
      setAction('');
      setRejectionReason('');

      // Refresh requests
      setTimeout(() => {
        fetchRequests();
      }, 1000);
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || `Failed to ${action} request.`;
      console.error(`Error ${action}ing request:`, err.response?.data ?? err);
      setError(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getUniqueCompanies = () => {
    const companies = [...new Set(requests.map(r => r.company))];
    return companies.sort();
  };

  if (isLoading) {
    return (
      <div className="ad-container">
        <div className="ad-loading">Loading pending requests...</div>
      </div>
    );
  }

  return (
    <div className="ad-container">
      <div className="ad-header">
        <div className="ad-header-content">
          <div className="ad-title-section">
            <h1 className="ad-title">Approver Dashboard</h1>
            <p className="ad-subtitle">Review and approve/reject role assignment requests</p>
          </div>
          <button
            onClick={fetchRequests}
            className="ad-refresh-btn"
            disabled={isLoading}
          >
            <FaSync /> Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="ad-error-banner">
          {error}
        </div>
      )}

      {success && (
        <div className="ad-success-banner">
          {success}
        </div>
      )}

      <div className="ad-stats">
        <div className="ad-stat-card">
          <div className="ad-stat-value">{requests.length}</div>
          <div className="ad-stat-label">Pending Requests</div>
        </div>
      </div>

      {requests.length > 0 && (
        <div className="ad-filters">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="ad-filter-select"
          >
            <option value="all">All Companies</option>
            {getUniqueCompanies().map(company => (
              <option key={company} value={company}>{company}</option>
            ))}
          </select>
        </div>
      )}

      <div className="ad-content">
        {filteredRequests.length === 0 ? (
          <div className="ad-empty-state">
            <FaCheckCircle className="ad-empty-icon" />
            <h2>No Pending Requests</h2>
            <p>You don't have any pending requests to review at the moment.</p>
          </div>
        ) : (
          <div className="ad-requests-list">
            {filteredRequests.map((request) => (
              <div key={request.id} className="ad-request-wrapper">
                <RequestCard request={request} showDetails={false} />
                <div className="ad-request-actions">
                  <div className="ad-request-info">
                    <div className="ad-info-item">
                      <FaUser />
                      <span>{request.requester}</span>
                    </div>
                    <div className="ad-info-item">
                      <FaBuilding />
                      <span>{request.company}</span>
                    </div>
                    <div className="ad-info-item">
                      <FaUserTie />
                      <span>{request.role}</span>
                    </div>
                    <div className="ad-info-item">
                      <span className="ad-step-badge">Step {request.current_step}: {request.step_name}</span>
                    </div>
                  </div>
                  {isOwnRequest(request) ? (
                    <div className="ad-action-buttons">
                      <span className="text-muted small">Your own request - another approver must act on it.</span>
                    </div>
                  ) : (
                    <div className="ad-action-buttons">
                      <button
                        onClick={() => handleApprove(request)}
                        className="ad-approve-btn"
                      >
                        <FaCheckCircle /> Approve
                      </button>
                      <button
                        onClick={() => handleReject(request)}
                        className="ad-reject-btn"
                      >
                        <FaTimesCircle /> Reject
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Approval/Rejection Modal */}
      {showModal && selectedRequest && (
        <div className="ad-modal-overlay" onClick={() => !isSubmitting && setShowModal(false)}>
          <div className="ad-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="ad-modal-header">
              <h2>
                {action === 'approve' ? 'Approve Request' : 'Reject Request'}
              </h2>
              <button
                className="ad-modal-close"
                onClick={() => !isSubmitting && setShowModal(false)}
                disabled={isSubmitting}
              >
                ×
              </button>
            </div>

            <div className="ad-modal-body">
              <div className="ad-modal-request-info">
                <p><strong>Requester:</strong> {selectedRequest.requester}</p>
                <p><strong>Company:</strong> {selectedRequest.company}</p>
                <p><strong>Role:</strong> {selectedRequest.role}</p>
                <p><strong>Current Step:</strong> Step {selectedRequest.current_step} - {selectedRequest.step_name}</p>
              </div>

              {action === 'reject' && (
                <div className="ad-modal-rejection-reason">
                  <label htmlFor="rejection-reason">
                    Rejection Reason (Optional)
                  </label>
                  <textarea
                    id="rejection-reason"
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="Enter reason for rejection..."
                    rows={4}
                    className="ad-reason-textarea"
                  />
                </div>
              )}

              {action === 'approve' && (
                <div className="ad-modal-approval-note">
                  <p>Are you sure you want to approve this request? It will be forwarded to the next approver in the chain.</p>
                </div>
              )}
            </div>

            <div className="ad-modal-footer">
              <button
                onClick={() => setShowModal(false)}
                className="ad-modal-cancel-btn"
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitAction}
                className={`ad-modal-submit-btn ${action === 'approve' ? 'approve' : 'reject'}`}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? 'Processing...'
                  : action === 'approve'
                  ? 'Approve'
                  : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApproverDashboard;
